import { useEffect } from "react";

import type { ExportFormat } from "../editor/types";
import { RangeControl, Switch } from "./controls";
import { Icon } from "./Icon";

export type ExportScope = "current" | "all";

interface ExportDialogProps {
  open: boolean;
  count: number;
  width: number;
  height: number;
  scope: ExportScope;
  busy: boolean;
  progress: string;
  format: ExportFormat;
  scale: number;
  stripMetadata: boolean;
  quality: number;
  removeBackground: boolean;
  autoDownload: boolean;
  lossy: boolean;
  onScopeChange: (scope: ExportScope) => void;
  onFormatChange: (format: ExportFormat) => void;
  onScaleChange: (scale: number) => void;
  onStripMetadataChange: (value: boolean) => void;
  onQualityChange: (value: number) => void;
  onRemoveBackgroundChange: (value: boolean) => void;
  onAutoDownloadChange: (value: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
}

const FORMAT_LABELS: Record<ExportFormat, string> = {
  original: "元の形式",
  png: "PNG",
  jpeg: "JPEG",
  webp: "WebP",
};

export function ExportDialog({
  open,
  count,
  width,
  height,
  scope,
  busy,
  progress,
  format,
  scale,
  stripMetadata,
  quality,
  removeBackground,
  autoDownload,
  lossy,
  onScopeChange,
  onFormatChange,
  onScaleChange,
  onStripMetadataChange,
  onQualityChange,
  onRemoveBackgroundChange,
  onAutoDownloadChange,
  onClose,
  onConfirm,
}: ExportDialogProps) {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;
  const outputWidth = Math.max(1, Math.round(width * scale));
  const outputHeight = Math.max(1, Math.round(height * scale));
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="export-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-heading">
          <div>
            <h2 id="export-title">ダウンロード</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="閉じる"
            disabled={busy}
            onClick={onClose}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {count > 1 && (
          <fieldset className="export-scope">
            <legend>書き出す画像</legend>
            <label>
              <input
                type="radio"
                name="scope"
                value="current"
                checked={scope === "current"}
                disabled={busy}
                onChange={() => onScopeChange("current")}
              />
              <span>
                <strong>現在の画像</strong>
                <small>開いている1枚をダウンロード</small>
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="scope"
                value="all"
                checked={scope === "all"}
                disabled={busy}
                onChange={() => onScopeChange("all")}
              />
              <span>
                <strong>すべての画像</strong>
                <small>{count}枚をZIPにまとめてダウンロード</small>
              </span>
            </label>
          </fieldset>
        )}

        <div className="export-options">
          <label className="export-field">
            <span className="export-label">ファイルの種類</span>
            <span className="export-select">
              <Icon name="image" size={18} />
              <select
                value={format}
                disabled={busy}
                aria-label="ファイルの種類"
                onChange={(event) =>
                  onFormatChange(event.target.value as ExportFormat)
                }
              >
                {Object.entries(FORMAT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <Icon name="chevron-down" size={16} />
            </span>
          </label>

          <div className="export-size-heading">
            <span className="export-label">サイズ</span>
            <span>
              {outputWidth.toLocaleString()} × {outputHeight.toLocaleString()} px
            </span>
          </div>
          <RangeControl
            label="書き出しサイズ"
            value={Math.round(scale * 100)}
            min={25}
            max={200}
            step={5}
            unit="%"
            disabled={busy}
            onChange={(value) => onScaleChange(value / 100)}
          />

          {lossy && (
            <RangeControl
              label="画質"
              value={quality}
              min={40}
              max={100}
              step={1}
              unit="%"
              disabled={busy}
              onChange={onQualityChange}
            />
          )}

          <div className="export-divider" />
          <Switch
            label="背景を透過"
            icon="image"
            checked={removeBackground}
            disabled={busy}
            onChange={onRemoveBackgroundChange}
          />
          <p className="export-hint">
            {removeBackground
              ? "端末内のAIで背景を削除します。初回のみ約4.7MBのモデルを読み込みます。"
              : "オンにすると人物や物体を判定して、背景を透明にします。"}
          </p>
          <Switch
            label="位置情報などのメタデータを削除"
            icon="shield"
            checked={stripMetadata}
            disabled={busy}
            onChange={onStripMetadataChange}
          />
          <p className="export-hint">
            {stripMetadata
              ? "撮影場所・撮影日時・カメラ情報を出力に残しません。"
              : "可能な場合は元画像のEXIFなどを維持します。形式変更やリサイズでは一部を維持できない場合があります。"}
          </p>
          {count > 1 && (
            <Switch
              label="画像を切り替えたときに自動でダウンロード"
              icon="download"
              checked={autoDownload}
              disabled={busy}
              onChange={onAutoDownloadChange}
            />
          )}
        </div>

        {progress && (
          <p className="export-progress" role="status">
            {progress}
          </p>
        )}
        <div className="dialog-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={busy}
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={busy}
            onClick={onConfirm}
          >
            <Icon name="download" size={16} />
            {busy ? "処理中…" : "ダウンロード"}
          </button>
        </div>
      </section>
    </div>
  );
}
